import { useState, useEffect } from 'react';
import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import { Code2, Layers, Brain, Zap, Database, Globe } from 'lucide-react';
import Eyebrow from './Eyebrow';
import { api } from '../api';

const ACCENT = '#5E0ED7';
const ease = [0.22, 1, 0.36, 1];

// Color palette by name — used when API returns color string
const COLOR_MAP = {
  blue:   { gradient: 'linear-gradient(135deg, #1d4ed8 0%, #4f46e5 100%)', chip: '#dbeafe', chipText: '#1d4ed8' },
  purple: { gradient: 'linear-gradient(135deg, #5E0ED7 0%, #7c3aed 100%)', chip: '#ede9fe', chipText: '#5E0ED7' },
  violet: { gradient: 'linear-gradient(135deg, #7c3aed 0%, #a21caf 100%)', chip: '#fae8ff', chipText: '#a21caf' },
  amber:  { gradient: 'linear-gradient(135deg, #d97706 0%, #ea580c 100%)', chip: '#fef3c7', chipText: '#d97706' },
  green:  { gradient: 'linear-gradient(135deg, #059669 0%, #0d9488 100%)', chip: '#d1fae5', chipText: '#059669' },
  cyan:   { gradient: 'linear-gradient(135deg, #0891b2 0%, #0284c7 100%)', chip: '#e0f2fe', chipText: '#0284c7' },
  red:    { gradient: 'linear-gradient(135deg, #dc2626 0%, #e11d48 100%)', chip: '#fee2e2', chipText: '#dc2626' },
  emerald:{ gradient: 'linear-gradient(135deg, #166534 0%, #15803d 100%)', chip: '#dcfce7', chipText: '#166534' },
};
const ICONS = [Code2, Layers, Brain, Zap, Database, Globe];

const DEFAULT_SERVICES = [
  { title: 'Custom Software Development', description: 'End-to-end engineering of web, mobile, and cloud applications. We build scalable systems your business can grow into.', tags: ['Web Apps', 'Mobile', 'Cloud', 'APIs'], color: 'blue' },
  { title: 'UX/UI Design & Product Strategy', description: 'Research-driven design systems and product strategies that convert users into advocates.', tags: ['Research', 'Prototyping', 'Design Systems'], color: 'purple' },
  { title: 'AI Consulting & Implementation', description: 'Practical AI adoption from strategy to production. LLMs, automation pipelines, and intelligent features that generate measurable ROI.', tags: ['LLMs', 'Automation', 'RAG', 'Agents'], color: 'violet' },
  { title: 'Digital Transformation', description: 'Modernize legacy systems and build digital infrastructure that positions your organization for the next decade.', tags: ['Legacy Migration', 'DevOps', 'Cloud'], color: 'amber' },
  { title: 'Data Engineering & Analytics', description: 'Build robust data pipelines, warehouses, and dashboards that give leadership clear visibility into what matters most.', tags: ['Data Pipelines', 'BI Dashboards', 'ETL'], color: 'green' },
  { title: 'Platform & API Development', description: 'Design and build the platform infrastructure that powers your product and enables partner growth.', tags: ['REST', 'GraphQL', 'Integrations'], color: 'cyan' },
];

function enrichService(s, i) {
  const colorStyle = COLOR_MAP[s.color] || COLOR_MAP.purple;
  const tags = Array.isArray(s.tags) ? s.tags : (typeof s.tags === 'string' ? s.tags.split(',').map(t=>t.trim()) : []);
  return { ...s, ...colorStyle, tags, icon: ICONS[i % ICONS.length] };
}

function ServiceCard({ service, index }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-80px' });
  const enriched = enrichService(service, index);
  const Icon = enriched.icon;

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ delay: index * 0.06, duration: 0.55, ease }}
      whileHover={{ y: -6, scale: 1.01 }}
      className="cursor-default h-full"
    >
      <div className="rounded-[1.75rem] h-full overflow-hidden" style={{ boxShadow: '0 4px 24px -6px rgba(0,0,0,0.12)' }}>
        {/* Gradient header */}
        <div className="h-28 relative flex items-end justify-between px-6 pb-5" style={{ background: enriched.gradient }}>
          <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ background: 'rgba(255,255,255,0.2)', backdropFilter: 'blur(8px)' }}>
            <Icon size={20} color="#fff" />
          </div>
          <div className="absolute top-4 right-4 w-20 h-20 rounded-full opacity-15" style={{ background: 'rgba(255,255,255,0.4)' }} />
        </div>

        {/* Body */}
        <div className="bg-white p-6 flex flex-col gap-3" style={{ minHeight: 200 }}>
          <h3 className="text-[15px] font-bold tracking-tight leading-snug" style={{ color: '#18181B', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            {enriched.title}
          </h3>
          <p className="text-sm leading-relaxed flex-1" style={{ color: '#6B6B72' }}>{enriched.description}</p>
          <div className="flex flex-wrap gap-1.5 mt-auto pt-3" style={{ borderTop: '1px solid rgba(24,24,27,0.06)' }}>
            {enriched.tags.map(t => (
              <span key={t} className="text-[11px] font-semibold px-2.5 py-1 rounded-full" style={{ background: enriched.chip, color: enriched.chipText }}>
                {t}
              </span>
            ))}
          </div>
        </div>
      </div>
    </motion.div>
  );
}

export default function Services() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-80px' });
  const [services, setServices] = useState(DEFAULT_SERVICES);

  useEffect(() => {
    api.getServices().then(d => { if (Array.isArray(d) && d.length) setServices(d); }).catch(() => {});
  }, []);

  return (
    <section id="expertise" className="px-5 sm:px-8 md:px-12 lg:px-20 flex flex-col justify-center py-24 section-mesh" style={{ minHeight: '100dvh' }}>
      <div className="max-w-7xl mx-auto w-full">
        <div ref={ref} className="mb-14">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.45, ease }}>
            <Eyebrow>What We Do</Eyebrow>
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 24 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.55, delay: 0.08, ease }}
            className="text-4xl md:text-5xl lg:text-[3.5rem] font-bold tracking-tight max-w-2xl leading-[1.08]"
            style={{ color: '#18181B', fontFamily: "'Plus Jakarta Sans', sans-serif", letterSpacing: '-0.025em' }}
          >
            Services Built for Scale
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5, delay: 0.16, ease }}
            className="text-lg mt-4 max-w-xl leading-relaxed"
            style={{ color: '#6B6B72' }}
          >
            From initial concept to market-ready product — every layer of the stack covered.
          </motion.p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 items-stretch">
          {services.map((s, i) => <ServiceCard key={s.title} service={s} index={i} />)}
        </div>
      </div>
    </section>
  );
}
